import * as Sentry from '@sentry/vue';
import { Globals } from '@/modules/Globals';

/** Tag of the GPTHelp widget custom element. */
export const GPTHELP_TAG_NAME = 'gpt-help';

/** The QueResto admin chatbot in GPTHelp. */
export const GPTHELP_CHATBOT_ID = '6ba9208c-c784-4604-a494-05aa0b65415d';

export interface IGpthelpWidgetBuild {
  src: string;
  /** Subresource Integrity hash (`sha384-…`); null loads the script without SRI. */
  integrity: string | null;
}

/**
 * The widget build the admin loads, pinned with its integrity. Bump only by
 * re-pinning a published build (gpthelp-nuxt public/plugin/v1/manifest.json).
 * Build 5734608056e7 = gpthelp-widget ec8e7fe (W-4).
 */
export const GPTHELP_WIDGET: IGpthelpWidgetBuild = {
  // The query only makes a Cloudflare cache key no request reached before the
  // build was live (a too-early request cached a 404 under the bare path).
  src: 'https://gpthelp.ai/plugin/v1/builds/5734608056e7/index.js?v=20261103',
  integrity:
    'sha384-9mLkYRWEHurIlomG3YEWB+R5fG70FNCMYnYcwGwHYpoQovGtF8sVyn9INjvbKn8a',
};

/** How long the element may take to be defined once the script is requested. */
const DEFINE_TIMEOUT_MS = 10_000;

/** Localhost-only overrides (never honoured in production). */
const WIDGET_SRC_OVERRIDE_KEY = 'gpthelp.widgetSrc';
const CHATBOT_ID_OVERRIDE_KEY = 'gpthelp.chatbotId';

function localOverride(key: string): string | null {
  if (!Globals.IsLocalhost) return null;
  try {
    return window.localStorage.getItem(key) || null;
  } catch {
    return null;
  }
}

/**
 * The build to load: the pinned one or, on localhost only, a local build
 * set with `localStorage.setItem('gpthelp.widgetSrc',
 * 'http://localhost:8099/dist/gpthelp-widget.js')` (loaded without SRI).
 */
export function gpthelpWidgetBuild(): IGpthelpWidgetBuild {
  const src = localOverride(WIDGET_SRC_OVERRIDE_KEY);
  return src ? { src, integrity: null } : GPTHELP_WIDGET;
}

/** The chatbot id or, on localhost only, `localStorage['gpthelp.chatbotId']`. */
export function gpthelpChatbotId(): string {
  return localOverride(CHATBOT_ID_OVERRIDE_KEY) ?? GPTHELP_CHATBOT_ID;
}

function widgetScripts(src: string): HTMLScriptElement[] {
  return Array.from(
    document.querySelectorAll<HTMLScriptElement>('script[data-gpthelp]')
  ).filter((script) => script.getAttribute('src') === src);
}

function loadWidget(build: IGpthelpWidgetBuild): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    const timeoutId = window.setTimeout(() => {
      reject(
        new Error(
          `<${GPTHELP_TAG_NAME}> not defined after ${DEFINE_TIMEOUT_MS} ms`
        )
      );
    }, DEFINE_TIMEOUT_MS);

    customElements.whenDefined(GPTHELP_TAG_NAME).then(() => {
      window.clearTimeout(timeoutId);
      resolve();
    });

    if (widgetScripts(build.src).length > 0) return;
    const script = document.createElement('script');
    script.src = build.src;
    script.async = true;
    script.dataset.gpthelp = '1';
    // SRI needs a CORS request; the unpinned alias keeps the plain request.
    if (build.integrity) {
      script.integrity = build.integrity;
      script.crossOrigin = 'anonymous';
    }
    script.addEventListener('error', () => {
      window.clearTimeout(timeoutId);
      reject(new Error(`Failed to load ${build.src}`));
    });
    document.head.appendChild(script);
  });
}

/** Shared by every caller (module scope); null again after a failure, so a retry reloads. */
let definePromise: Promise<void> | null = null;
/** A load failure reaches Sentry once per page load (the panel and the bubble share it). */
let loadFailureReported = false;

function reportLoadFailure(build: IGpthelpWidgetBuild, error: unknown): void {
  if (loadFailureReported) return;
  loadFailureReported = true;
  Sentry.captureMessage('Assistant widget failed to load', {
    level: 'warning',
    tags: { feature: 'assistant' },
    extra: {
      src: build.src,
      error: error instanceof Error ? error.message : String(error),
    },
  });
}

/**
 * Loads the widget script once and resolves when `<gpt-help>` is defined
 * (within 10 s). A failed or timed-out load removes its script, so the next
 * call tries again. Callers check `isGpthelpSupportedBrowser()` first.
 */
export function ensureGpthelpDefined(): Promise<void> {
  if (customElements.get(GPTHELP_TAG_NAME)) return Promise.resolve();
  if (!definePromise) {
    const build = gpthelpWidgetBuild();
    definePromise = loadWidget(build).catch((error: unknown) => {
      definePromise = null;
      widgetScripts(build.src).forEach((script) => script.remove());
      reportLoadFailure(build, error);
      throw error;
    });
  }
  return definePromise;
}
